// scenario 的兩個外部入口：URL query（§16.2）與 Playwright 注入（§16.3）。
//
// §16.3 的四條要求對應到這個檔案：
//   1. 只在 development／test／storybook 開啟 → 一律先過 isScenarioInjectionEnabled()。
//   2. production build 必須可移除 → 判斷值來自 import.meta.env.MODE，production build 時
//      是編譯期常數；模組本身沒有任何 side effect，未被引用的分支可被 tree-shaking 移除。
//   3. 不得讓測試直接修改元件內部實作細節 → 這裡只吐出 { scenario, role } 這個公開契約，
//      沒有任何元件 ref、內部 state setter 或 DOM hook 外流。
//   4. 必須以公開 scenario 契約驅動畫面 → MockProvider 只吃這個契約，其他一概不看。
//
// URL query 與 window 注入的差別（刻意的）：
//   - window 注入 **有** mode 閘門：它是測試專用的執行期後門，production 不該存在。
//   - URL query **沒有** mode 閘門：`?scenario=empty` 是雛形本身要提供的展示能力，
//     `bun run build` 出來的 demo 站台也要能用。§16.2 只要求「正常 default scenario
//     不顯示驗證控制介面」——沒有參數時本模組回傳空物件，畫面上不會多出任何東西。

import type { Role, ScenarioState } from '@/mocks/types';
import { isScenarioInjectionEnabled } from '@/test-support/ui-id';

/** Playwright／Storybook 注入的公開契約。三個欄位都是選填。 */
export interface ScenarioInjection {
  scenario?: ScenarioState;
  role?: Role;
  /**
   * FC-5 R4（C1-c‴）：同一頁同一動作底下有多條 legacy 訊息互相碰撞時，用這個欄位指定
   * 要看哪一條（`legacy-texts.generated.ts` 的 item key，例如 `legacy.shared.25`）。
   * 沒帶這個欄位時退回既有的「動作關鍵字」自動挑選（R3 行為不變）。這是既有 scenario
   * 注入契約（§16.2／16.3）的延伸，不是新機制——跟 `scenario`／`role` 走同一條入口
   * （URL query 或 Playwright window 注入），只是多一個欄位。
   */
  messageKey?: string;
}

/** window 上的掛載點名稱。Playwright init script 只需要知道這個字串。 */
export const SCENARIO_INJECTION_KEY = '__uiScenario';

declare global {
  interface Window {
    __uiScenario?: ScenarioInjection;
  }
}

// Record<ScenarioState, true> 會在 types.ts 增減 scenario 時直接編譯失敗，
// 比手抄一份陣列可靠（陣列漏一個沒人會發現）。
const SCENARIO_STATE_FLAGS: Readonly<Record<ScenarioState, true>> = {
  default: true,
  populated: true,
  empty: true,
  loading: true,
  error: true,
  'validation-error': true,
  create: true,
  edit: true,
  'view-only': true,
  confirmation: true,
  success: true,
  unauthorized: true,
  filter: true,
  pagination: true,
  export: true,
  'write-error': true,
};

const ROLE_FLAGS: Readonly<Record<Role, true>> = {
  admin: true,
  teacher: true,
  student: true,
  mentor: true,
};

/** 全部合法的 scenario 值，可直接餵給 Storybook 的 select control 或測試參數化。 */
export const SCENARIO_STATES = Object.keys(SCENARIO_STATE_FLAGS) as readonly ScenarioState[];

export const ROLES = Object.keys(ROLE_FLAGS) as readonly Role[];

export function isScenarioState(value: unknown): value is ScenarioState {
  return typeof value === 'string' && Object.hasOwn(SCENARIO_STATE_FLAGS, value);
}

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && Object.hasOwn(ROLE_FLAGS, value);
}

/**
 * `messageKey` 的形狀檢查（`legacy.<tier>.<兩位數字>`）——只驗證形狀，不驗證這個 key
 * 真的存在。真的存不存在、對不對得上目前的頁面／分類，由 `mock-data-source.ts` 決定
 * （那裡才有 `legacy-texts.generated.ts` 的資料，這個檔案刻意不依賴它，維持 §16.3
 * 「不得讓測試直接修改元件內部實作細節」的最小公開契約）。
 */
function isMessageKeyShape(value: unknown): value is string {
  return typeof value === 'string' && /^legacy\.(global|shared|unique)\.\d{2}$/.test(value);
}

function normalize(candidate: {
  scenario?: unknown;
  role?: unknown;
  messageKey?: unknown;
}): ScenarioInjection {
  const result: ScenarioInjection = {};
  if (isScenarioState(candidate.scenario)) {
    result.scenario = candidate.scenario;
  }
  if (isRole(candidate.role)) {
    result.role = candidate.role;
  }
  if (isMessageKeyShape(candidate.messageKey)) {
    result.messageKey = candidate.messageKey;
  }
  return result;
}

/**
 * 讀取 Playwright／Storybook 注入的 scenario。
 *
 * `mode` 預設取 import.meta.env.MODE；測試要驗 production 行為時直接傳 'production'。
 * 非 development／test／storybook 一律回傳空物件——注入後門在 production 不存在。
 */
export function readInjectedScenario(mode: string = import.meta.env.MODE): ScenarioInjection {
  // 這裡刻意「直接」比較 import.meta.env.MODE，而不是呼叫 isScenarioInjectionEnabled()：
  // Vite 只會在來源碼裡把 import.meta.env.MODE 換成字面常數，換完 minifier 才折得掉。
  // 跨模組的 isScenarioInjectionEnabled(import.meta.env.MODE) 折不掉——實測過，
  // '__uiScenario' 字串會留在 production bundle 裡。
  // 用 vite define 也無效：define 的替換文字不會被遞迴處理，import.meta.env.MODE 會原樣輸出。
  if (import.meta.env.MODE === 'production') {
    return {};
  }
  if (!isScenarioInjectionEnabled(mode)) {
    return {};
  }
  if (typeof window === 'undefined') {
    return {};
  }
  const injected = window[SCENARIO_INJECTION_KEY];
  if (injected === undefined || injected === null || typeof injected !== 'object') {
    return {};
  }
  return normalize(injected);
}

/** 讀取 §16.2 的 URL query，例如 `?scenario=empty&role=admin`。無參數時回傳空物件。 */
export function readScenarioFromSearch(search: string): ScenarioInjection {
  const params = new URLSearchParams(search);
  return normalize({
    scenario: params.get('scenario') ?? undefined,
    role: params.get('role') ?? undefined,
    messageKey: params.get('messageKey') ?? undefined,
  });
}

/**
 * 產生給 Playwright `page.addInitScript()` 用的字串。
 * 用回傳字串而不是回傳函式，是因為 addInitScript 會把函式序列化送進瀏覽器，
 * 序列化後參照不到模組層級的識別字（同 pii-guard.collectVisibleTexts 的坑）。
 *
 * 用法：
 *   await page.addInitScript(scenarioInitScript({ scenario: 'empty', role: 'admin' }));
 */
export function scenarioInitScript(injection: ScenarioInjection): string {
  return `window[${JSON.stringify(SCENARIO_INJECTION_KEY)}] = ${JSON.stringify(injection)};`;
}

/**
 * 在瀏覽器端直接設定注入值（Storybook decorator／單元測試用）。
 * mode 不允許時什麼都不做，避免有人拿它當 production 的功能開關。
 */
export function applyScenarioInjection(
  injection: ScenarioInjection,
  mode: string = import.meta.env.MODE,
): void {
  if (!isScenarioInjectionEnabled(mode) || typeof window === 'undefined') {
    return;
  }
  window[SCENARIO_INJECTION_KEY] = injection;
}

/** 清掉注入值（測試 afterEach 用）。 */
export function clearScenarioInjection(): void {
  if (typeof window !== 'undefined') {
    delete window[SCENARIO_INJECTION_KEY];
  }
}
